import { FeatureLineup } from '@mdlbeasts/nofomo.ui.web';

import { DateFormat, i18nFormat } from 'utils/date.utils';
import { getEntryBilling, LineupSettings } from 'utils/lineup.utils';

import { SiteLocale } from 'constants/i18n.constants';
import {
  PARTNER_TIERS,
  PartnerTier,
  ProgramSort,
  XP_STAGE_COLOR,
} from 'constants/lineup.constants';
import {
  LineupEntryFragment,
  LineupEntryQuery,
  LineupSelectionQuery,
  LineupXpArtistQuery,
  LineupXpSessionQuery,
  LineupXpSpeakerQuery,
  XpPartnerFragment,
  XpSessionArtistsFragment,
  XpSessionFragment,
  XpSessionPartnersFragment,
  XpSessionSpeakersFragment,
  XpSpeakerFragment,
} from 'types/api/dato-operations.types';

import {
  compareByStart,
  endOf,
  groupByDay,
  slotOf,
  startOf,
  Timed,
  zoneOf,
} from 'components/@lineup/utils';

/** Prefixed so it sorts after every real key. */
const UNGROUPED = 'zz-ungrouped';

/** Named records as one line, e.g. "Talk, Workshop". */
const namesOf = (records: Array<{ name: string }>) =>
  records.map((record) => record.name).join(', ') || undefined;

const dayOf = (
  start: Date | undefined,
  formatString: string,
  locale: SiteLocale,
  { hideDates }: LineupSettings,
) =>
  start && !hideDates ? i18nFormat(start, formatString, locale) : undefined;

/** A row says when and where, so hiding all of it leaves no row to show. */
const hasSlot = ({ hideDates, hideTimes, hideStages }: LineupSettings) =>
  !(hideDates && hideTimes && hideStages);

const timeOf = (
  item: Timed,
  locale: SiteLocale,
  timezone: string,
  { hideTimes }: LineupSettings,
) => (hideTimes ? undefined : slotOf(item, locale, timezone));

interface ToProgramSectionsOptions {
  sessions: Array<XpSessionFragment> | undefined;
  locale: SiteLocale;
  timezone: string | undefined;
  settings: LineupSettings;
  sort: ProgramSort;
  /** Heading for sessions with no date yet. */
  undatedLabel: string;
  /** Heading for sessions the sort cannot group. */
  ungroupedLabel: string;
}

/**
 * A list because genres are many: a house-and-techno session shows under both.
 * The key only orders the sections, so dates sort as `yyyy-MM-dd` while reading
 * as "Thursday, Dec 04".
 */
const sectionsOf = (
  session: XpSessionFragment,
  { locale, sort, undatedLabel, ungroupedLabel }: ToProgramSectionsOptions,
  timezone: string,
): Array<{ key: string; label: string }> => {
  if (sort === 'stage') {
    return [{ key: session.stage.name, label: session.stage.name }];
  }

  if (sort === 'genre') {
    return session.genres.length
      ? session.genres.map((genre) => ({ key: genre.name, label: genre.name }))
      : [{ key: UNGROUPED, label: ungroupedLabel }];
  }

  const start = startOf(session, timezone);

  return [
    start
      ? {
          key: i18nFormat(start, DateFormat.DayKey, locale),
          label: i18nFormat(start, DateFormat.WeekdayDate[locale], locale),
        }
      : { key: UNGROUPED, label: undatedLabel },
  ];
};

export const toEntrySession = (
  session: XpSessionFragment,
  locale: SiteLocale,
  timezone: string,
  settings: LineupSettings,
): FeatureLineup.Xp.ProgramSession => {
  const start = startOf(session, timezone);

  return {
    id: session.id,
    title: session.title,
    stage: settings.hideStages ? undefined : session.stage.name,
    stageColor: settings.hideStages
      ? undefined
      : (session.stage.color?.hex ?? XP_STAGE_COLOR),
    stageLogo: settings.hideStages ? undefined : session.stage.logo?.url,
    types: namesOf(session.sessionTypes),
    interests: namesOf(session.interests),
    // Repeats the date heading, which is how the design has it.
    day: dayOf(start, DateFormat.Weekday, locale, settings),
    time: timeOf(session, locale, timezone, settings),
    isTimeHidden: settings.hideTimes,
  };
};

/** The sort picks what groups the sessions, not just their order. */
export const toProgramSections = (
  options: ToProgramSectionsOptions,
): Array<FeatureLineup.Xp.ProgramSection> => {
  const { sessions, timezone } = options;

  if (!sessions?.length) return [];

  const zone = zoneOf(timezone);

  const sections = new Map<
    string,
    { label: string; sessions: Array<XpSessionFragment> }
  >();

  for (const session of sessions) {
    for (const { key, label } of sectionsOf(session, options, zone)) {
      const section = sections.get(key) ?? { label, sessions: [] };

      section.sessions.push(session);
      sections.set(key, section);
    }
  }

  return [...sections.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, section]) => ({
      id: key,
      label: section.label,
      sessions: section.sessions
        .sort(compareByStart(zone))
        .map((session) =>
          toEntrySession(session, options.locale, zone, options.settings),
        ),
    }));
};

export const toSpeaker = (
  speaker: XpSpeakerFragment,
): FeatureLineup.Standard.PerformerItem => ({
  id: speaker.id,
  name: speaker.name,
  subtitle: speaker.role,
  image: speaker.picture?.url,
});

interface ToSpeakersOptions {
  sessions: Array<XpSessionSpeakersFragment> | undefined;
}

/**
 * Speakers hang off sessions, and one can hold several, so they are billed by
 * their role rather than by a slot. Left unsorted: the view sorts A-Z itself.
 */
export const toSpeakers = ({
  sessions,
}: ToSpeakersOptions): Array<FeatureLineup.Standard.PerformerItem> => {
  const speakers = new Map<string, FeatureLineup.Standard.PerformerItem>();

  for (const session of sessions ?? []) {
    for (const speaker of session.speakers) {
      if (!speakers.has(speaker.id))
        speakers.set(speaker.id, toSpeaker(speaker));
    }
  }

  return [...speakers.values()];
};

interface ToPartnersOptions {
  sessions: Array<XpSessionPartnersFragment> | undefined;
  /** Tier name per tier, already translated. */
  tierLabels: Record<string, string>;
}

export const toPartner = (
  partner: XpPartnerFragment,
  tierLabels: Record<string, string>,
): FeatureLineup.Xp.Partner => ({
  id: partner.id,
  name: partner.name,
  logo: partner.logo?.url,
  subtitle: tierLabels[partner.tier],
});

/** Unknown tiers fall behind the ranked ones. */
const rankOf = (tier: string) => {
  const rank = PARTNER_TIERS.indexOf(tier as PartnerTier);

  return rank < 0 ? PARTNER_TIERS.length : rank;
};

/** Richest tier first, then by name: the view renders the list as given. */
export const toPartners = ({
  sessions,
  tierLabels,
}: ToPartnersOptions): Array<FeatureLineup.Xp.Partner> => {
  const partners = new Map<string, XpPartnerFragment>();

  for (const session of sessions ?? []) {
    for (const partner of session.partners) {
      if (!partners.has(partner.id)) partners.set(partner.id, partner);
    }
  }

  return [...partners.values()]
    .sort(
      (a, b) => rankOf(a.tier) - rankOf(b.tier) || a.name.localeCompare(b.name),
    )
    .map((partner) => toPartner(partner, tierLabels));
};

export interface TimeTableDay {
  id: string;
  /** Day heading, e.g. "Thursday 4". */
  label: string;
  /** Second line under it, e.g. "Dec 2026". */
  sublabel: string;
  rooms: Array<FeatureLineup.Xp.TimeTableRoom>;
  sessions: Array<FeatureLineup.Xp.TimeTableSession>;
}

interface ToTimeTableDaysOptions {
  sessions: Array<XpSessionFragment> | undefined;
  locale: SiteLocale;
  timezone: string | undefined;
  settings: LineupSettings;
}

/** Only the stages running that day get a column, so a grid is never half empty. */
const columnsOf = (
  items: Array<{
    stage?: {
      id: string;
      name: string;
      color?: { hex: string } | null;
      logo?: { url: string } | null;
    } | null;
  }>,
  settings: LineupSettings,
  fallbackColor?: string,
) => {
  const stages = new Map<
    string,
    { id: string; label: string; color?: string; logo?: string }
  >();

  for (const { stage } of items) {
    if (stage && !stages.has(stage.id))
      // A column still holds its sets, so a hidden stage loses only its name.
      stages.set(stage.id, {
        id: stage.id,
        label: settings.hideStages ? '' : stage.name,
        color: settings.hideStages
          ? undefined
          : (stage.color?.hex ?? fallbackColor),
        logo: settings.hideStages ? undefined : stage.logo?.url,
      });
  }

  return [...stages.values()].sort((a, b) => a.label.localeCompare(b.label));
};

/** Dates are pre-zoned, since the grid reads the hours off them for its axis. */
export const toTimeTableDays = ({
  sessions,
  locale,
  timezone,
  settings,
}: ToTimeTableDaysOptions): Array<TimeTableDay> => {
  const zone = zoneOf(timezone);

  return groupByDay(sessions ?? [], locale, zone).map((day) => ({
    id: day.id,
    label: day.label,
    sublabel: day.sublabel,
    rooms: columnsOf(
      day.items.map(({ value }) => value),
      settings,
      XP_STAGE_COLOR,
    ),
    sessions: day.items.map(({ value: session, start }) => ({
      id: session.id,
      roomId: session.stage.id,
      title: session.title,
      start,
      end: endOf(session, zone),
      time: timeOf(session, locale, zone, settings),
      isTimeHidden: settings.hideTimes,
      types: namesOf(session.sessionTypes),
      interests: namesOf(session.interests),
    })),
  }));
};

interface ToSessionDetailOptions {
  session: NonNullable<LineupXpSessionQuery['xpSession']> | undefined;
  locale: SiteLocale;
  timezone: string | undefined;
  settings: LineupSettings;
  /** Tier name per tier, already translated. */
  tierLabels: Record<string, string>;
}

/**
 * The sheet takes a list of slots, but a record holds one start and one end,
 * so the timetable is that single slot.
 */
export const toSessionDetail = ({
  session,
  locale,
  timezone,
  settings,
  tierLabels,
}: ToSessionDetailOptions) => {
  if (!session) return undefined;

  const zone = zoneOf(timezone);
  const start = startOf(session, zone);

  let timetable = [
    {
      id: session.id,
      day: dayOf(start, DateFormat.WeekdayDay, locale, settings),
      time: timeOf(session, locale, zone, settings),
      isTimeHidden: settings.hideTimes,
      stage: settings.hideStages ? undefined : session.stage.name,
      stageColor: settings.hideStages
        ? undefined
        : (session.stage.color?.hex ?? XP_STAGE_COLOR),
      stageLogo: settings.hideStages ? undefined : session.stage.logo?.url,
    },
  ];

  if (!hasSlot(settings)) timetable = [];

  return {
    title: session.title,
    types: namesOf(session.sessionTypes),
    interests: namesOf(session.interests),
    timetable,
    speakers: session.speakers.map(toSpeaker),
    partners: session.partners.map((partner) => toPartner(partner, tierLabels)),
  };
};

interface ToSpeakerDetailOptions {
  speaker: NonNullable<LineupXpSpeakerQuery['xpSpeaker']> | undefined;
  locale: SiteLocale;
  timezone: string | undefined;
  settings: LineupSettings;
}

/** Their sessions are the same cards the Programs view shows. */
export const toSpeakerDetail = ({
  speaker,
  locale,
  timezone,
  settings,
}: ToSpeakerDetailOptions) => {
  if (!speaker) return undefined;

  const zone = zoneOf(timezone);

  return {
    name: speaker.name,
    subtitle: speaker.role,
    image: speaker.picture?.url,
    sessions: speaker.sessions
      .filter((session) => session.programs.length > 0)
      .map((session) => toEntrySession(session, locale, zone, settings)),
  };
};

type SelectionProgram = NonNullable<
  LineupSelectionQuery['pageEvent']
>['programs'][number];

interface ToSelectionProgramsOptions {
  programs: Array<SelectionProgram> | undefined;
  /** Faces shown before the rest become a count. */
  billing: number;
  /** Reads the remainder, e.g. "9 others". */
  othersLabel: (count: number) => string;
}

/**
 * A programme is billed by whoever is on it, so speakers and artists are
 * collected off its sessions and deduplicated.
 */
export const toSelectionPrograms = ({
  programs,
  billing,
  othersLabel,
}: ToSelectionProgramsOptions): Array<FeatureLineup.Selection.SelectionProgram> =>
  (programs ?? []).map((program) => {
    const people = new Map<string, { name: string; image?: string }>();

    for (const session of program.sessions) {
      const sessionPeople =
        program.kind === 'nite' ? session.artists : session.speakers;

      for (const person of sessionPeople) {
        if (!people.has(person.id))
          people.set(person.id, {
            name: person.name,
            image: person.picture?.url,
          });
      }
    }

    const billed = [...people.values()].slice(0, billing);
    const rest = people.size - billed.length;

    return {
      id: program.id,
      name: program.name,
      subtitle:
        [...billed.map((person) => person.name), rest ? othersLabel(rest) : '']
          .filter(Boolean)
          .join(', ') || undefined,
      images: billed
        .map((person) => person.image)
        .filter((image): image is string => !!image),
    };
  });

interface ToEntriesOptions {
  entries: Array<LineupEntryFragment> | undefined;
  locale: SiteLocale;
  timezone: string | undefined;
  settings: LineupSettings;
}

/** Branding for this event first, the artist's own picture behind it. */
const pictureOf = (entry: LineupEntryFragment) =>
  entry.artistPicture?.url ?? entry.performer.picture?.url;

/** A b2b is billed by its artists, so the row stacks their portraits. */
const picturesOf = (entry: LineupEntryFragment) => {
  if (!entry.collaborators.length) return undefined;

  return [entry.performer, ...entry.collaborators]
    .map((artist) => artist.picture?.url)
    .filter((url): url is string => !!url);
};

/** One row per set, so an artist playing twice is listed twice. */
export const toLineupPerformers = ({
  entries,
  locale,
  timezone,
  settings,
}: ToEntriesOptions): Array<FeatureLineup.Standard.PerformerItem> => {
  const zone = zoneOf(timezone);

  return (entries ?? []).map((entry) => {
    const start = startOf(entry, zone);

    return {
      id: entry.id,
      name: getEntryBilling(entry),
      image: pictureOf(entry),
      images: picturesOf(entry),
      day: dayOf(start, DateFormat.Weekday, locale, settings),
      time: timeOf(entry, locale, zone, settings),
      isTimeHidden: settings.hideTimes,
    };
  });
};

/** The headliners (or the full lineup if none), in running order rather than A-Z. */
export const toFeaturedArtists = ({
  entries,
  locale,
  timezone,
  settings,
}: ToEntriesOptions): Array<FeatureLineup.Standard.FeaturedItem> => {
  const zone = zoneOf(timezone);

  const all = entries ?? [];
  const headliners = all.filter((entry) => entry.headliner);

  // Fall back to the full lineup when no one is billed as a headliner.
  return (headliners.length ? headliners : all)
    .sort(compareByStart(zone))
    .map((entry) => {
      const start = startOf(entry, zone);

      return {
        id: entry.id,
        name: getEntryBilling(entry),
        image: pictureOf(entry),
        month: dayOf(start, DateFormat.Month[locale], locale, settings),
        day: dayOf(start, DateFormat.DayOfMonth, locale, settings),
        time: timeOf(entry, locale, zone, settings),
        isTimeHidden: settings.hideTimes,
      };
    });
};

export interface LineupTimeTableDay {
  id: string;
  label: string;
  sublabel: string;
  stages: Array<FeatureLineup.Standard.TimeTableStage>;
  sets: Array<FeatureLineup.Standard.TimeTableSet>;
}

/** The standard counterpart of `toTimeTableDays`. Stageless sets have no column. */
export const toLineupTimeTableDays = ({
  entries,
  locale,
  timezone,
  settings,
}: ToEntriesOptions): Array<LineupTimeTableDay> => {
  const zone = zoneOf(timezone);
  const placed = (entries ?? []).filter((entry) => !!entry.stage);

  return groupByDay(placed, locale, zone).map((day) => ({
    id: day.id,
    label: day.label,
    sublabel: day.sublabel,
    stages: columnsOf(
      day.items.map(({ value }) => value),
      settings,
    ),
    sets: day.items.map(({ value: entry, start }) => ({
      id: entry.id,
      stageId: `${entry.stage?.id}`,
      name: getEntryBilling(entry),
      start,
      end: endOf(entry, zone),
      time: timeOf(entry, locale, zone, settings),
      isTimeHidden: settings.hideTimes,
    })),
  }));
};

interface ToEntryArtistOptions {
  entry: NonNullable<LineupEntryQuery['lineupEntry']> | undefined;
  artistId: string | undefined;
}

/** One artist of a b2b, billed by themselves rather than by the set. */
export const toEntryArtist = ({ entry, artistId }: ToEntryArtistOptions) => {
  if (!entry?.collaborators.length || !artistId) return undefined;

  const billed = [entry.performer, ...entry.collaborators];
  const artist = billed.find((candidate) => candidate.id === artistId);

  if (!artist) return undefined;

  return {
    name: artist.name,
    slug: artist.slug,
    image: artist.picture?.url,
    biography: artist.biography,
  };
};

interface ToEntryDetailOptions {
  entry: NonNullable<LineupEntryQuery['lineupEntry']> | undefined;
  locale: SiteLocale;
  timezone: string | undefined;
  settings: LineupSettings;
}

/**
 * A single artist is billed by their own hero, biography and whole run. A b2b
 * is a set rather than a person, so it keeps only the name, the slot and who
 * plays it.
 */
export const toEntryDetail = ({
  entry,
  locale,
  timezone,
  settings,
}: ToEntryDetailOptions) => {
  if (!entry) return undefined;

  const zone = zoneOf(timezone);
  const start = startOf(entry, zone);

  let timetable = entry.performer.sets.map((set) => {
    const setStart = startOf(set, zone);

    return {
      id: set.id,
      day: dayOf(setStart, DateFormat.WeekdayDay, locale, settings),
      time: timeOf(set, locale, zone, settings),
      isTimeHidden: settings.hideTimes,
      stage: settings.hideStages ? undefined : set.stage?.name,
      stageColor: settings.hideStages ? undefined : set.stage?.color?.hex,
      stageLogo: settings.hideStages ? undefined : set.stage?.logo?.url,
    };
  });

  if (!hasSlot(settings)) timetable = [];

  const detail = {
    isB2B: false,
    name: entry.performer.name,
    image: pictureOf(entry),
    day: dayOf(start, DateFormat.Weekday, locale, settings),
    time: timeOf(entry, locale, zone, settings),
    artists: [] as Array<{ id: string; name: string; image?: string }>,
    timetable,
  };

  if (!entry.collaborators.length) return detail;

  const billed = [entry.performer, ...entry.collaborators];

  return {
    ...detail,
    isB2B: true,
    name: getEntryBilling(entry),
    image: undefined,
    artists: billed.map((artist) => ({
      id: artist.id,
      name: artist.name,
      image: artist.picture?.url,
    })),
    timetable: [],
  };
};

interface ToArtistsOptions {
  sessions: Array<XpSessionArtistsFragment> | undefined;
  locale: SiteLocale;
  timezone: string | undefined;
  settings: LineupSettings;
}

/** The nite counterpart of `toSpeakers`. Genres sit on the sessions, not here. */
export const toArtists = ({
  sessions,
  locale,
  timezone,
  settings,
}: ToArtistsOptions): Array<FeatureLineup.Standard.PerformerItem> => {
  const zone = zoneOf(timezone);
  const artists = new Map<string, FeatureLineup.Standard.PerformerItem>();

  for (const session of [...(sessions ?? [])].sort(compareByStart(zone))) {
    const start = startOf(session, zone);

    for (const artist of session.artists) {
      if (artists.has(artist.id)) continue;

      artists.set(artist.id, {
        id: artist.id,
        name: artist.name,
        image: artist.picture?.url,
        day: dayOf(start, DateFormat.Weekday, locale, settings),
        time: timeOf(session, locale, zone, settings),
        isTimeHidden: settings.hideTimes,
      });
    }
  }

  return [...artists.values()];
};

interface ToArtistDetailOptions {
  artist: NonNullable<LineupXpArtistQuery['artist']> | undefined;
  locale: SiteLocale;
  timezone: string | undefined;
  settings: LineupSettings;
}

/**
 * Sets read as timetable rows, so each carries only when and where. Genres are
 * collected off those sets, since the artist record has none.
 */
export const toArtistDetail = ({
  artist,
  locale,
  timezone,
  settings,
}: ToArtistDetailOptions) => {
  if (!artist) return undefined;

  const zone = zoneOf(timezone);

  let sessions = artist.sessions.filter(
    (session) => session.programs.length > 0,
  );

  const genres = [
    ...new Set(
      sessions.flatMap((session) => session.genres.map((genre) => genre.name)),
    ),
  ];

  if (!hasSlot(settings)) sessions = [];

  return {
    name: artist.name,
    image: artist.picture?.url,
    subtitle: genres.join(', ') || undefined,
    sessions: sessions.map((session) => {
      const start = startOf(session, zone);

      return {
        id: session.id,
        // A set is known by its artist, named above.
        day: dayOf(start, DateFormat.WeekdayDay, locale, settings),
        time: timeOf(session, locale, zone, settings),
        isTimeHidden: settings.hideTimes,
        stage: settings.hideStages ? undefined : session.stage.name,
        stageColor: settings.hideStages
          ? undefined
          : (session.stage.color?.hex ?? XP_STAGE_COLOR),
        stageLogo: settings.hideStages ? undefined : session.stage.logo?.url,
      };
    }),
  };
};
